import React, { createContext, useState, useContext, useEffect } from 'react';
import { cartAPI } from '../services/api';
import { useAuth } from './AuthContext';
import { useLanguage } from './LanguageContext';
import { toast } from 'react-toastify';

const CartContext = createContext();

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

export const CartProvider = ({ children }) => {
  const [cart, setCart] = useState({ items: [], total: 0, item_count: 0 });
  const [loading, setLoading] = useState(false);
  const { isAuthenticated } = useAuth();
  const { t } = useLanguage();

  const fetchCart = async () => {
    if (!isAuthenticated()) {
      setCart({ items: [], total: 0, item_count: 0 });
      return;
    }

    try {
      setLoading(true);
      const response = await cartAPI.get();
      setCart(response.data);
    } catch (error) {
      console.error('Error fetching cart:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchCart();
  }, [isAuthenticated()]);

  const addToCart = async (productId, quantity = 1) => {
    try {
      await cartAPI.addItem({ product_id: productId, quantity });
      await fetchCart();
      toast.success(t('toast.addedToCart'), { autoClose: 1000 });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add to cart';
      toast.error(message, { autoClose: 1000 });
      throw error;
    }
  };

  const updateCartItem = async (itemId, quantity) => {
    try {
      await cartAPI.updateItem(itemId, { quantity });
      await fetchCart();
      toast.success(t('toast.cartUpdated'), { autoClose: 1000 });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to update cart';
      toast.error(message, { autoClose: 1000 });
      throw error;
    }
  };

  const removeFromCart = async (itemId) => {
    try {
      await cartAPI.removeItem(itemId);
      await fetchCart();
      toast.success(t('toast.removedFromCart'), { autoClose: 1000 });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to remove item';
      toast.error(message, { autoClose: 1000 });
      throw error;
    }
  };

  const clearCart = async () => {
    try {
      await cartAPI.clear();
      await fetchCart();
      toast.success(t('toast.cartCleared'), { autoClose: 1000 });
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to clear cart';
      toast.error(message, { autoClose: 1000 });
      throw error;
    }
  };

  const value = {
    cart,
    loading,
    addToCart,
    updateCartItem,
    removeFromCart,
    clearCart,
    refreshCart: fetchCart,
  };

  return <CartContext.Provider value={value}>{children}</CartContext.Provider>;
};
